import { createElement } from 'react';
import type { StickyHeaderAction } from './StickyHeader';
import { DocumentArrowDownIcon } from './chromeIcons';

/**
 * approvalGuard가 반환하는 작성자 결재 액션 게이트.
 * (2026-07-26 web reportActionPolicy → rc 이전 — 계산(guard)은 web 소유,
 *  표시(buildApprovalActions)와 계약 타입은 rc 공유. web은 재수출로 하위호환.)
 */
export interface ApprovalGuardResult {
  /** serverHasApprovalLinePolicy===false면 'completion'(완료 UX), 그 외 'approval'. */
  mode: 'approval' | 'completion';
  /** 결재 chrome 자체 숨김(APPROVED 등). */
  hidden: boolean;
  /**
   * 결재/완료 제출 또는 재제출.
   * `busy`는 요청 진행 중(제출/재제출) — 트리거에 스피너를 띄우기 위한 신호다.
   * 상신 응답은 알림 발송을 기다리느라 수 초가 걸릴 수 있어(2026-07-23 실측 4.07초),
   * 비활성만으로는 "눌리지 않는다"로만 보이고 진행 중임을 알 수 없다.
   */
  submit: { enabled: boolean; reason?: string; busy?: boolean; onClick: () => void };
  /** 결재 취소(null=미지원, completion 모드 등). */
  cancel: { enabled: boolean; reason?: string; onClick: () => void } | null;
  /** 결재 재전송(null=미지원). */
  resend: { enabled: boolean; reason?: string; onClick: () => void } | null;
}

/**
 * 작성자 결재 액션을 단일 `결재 ▼`(또는 완료 모드는 `완료` 단일 버튼)으로 통합하는 빌더(2026-06-15).
 *
 * - guard.hidden → []
 * - guard.mode === 'completion' → 단일 `완료` 버튼(드롭다운 아님, 결재라인 비활성 type)
 * - guard.mode === 'approval' → `결재 ▼` 트리거 + menuItems(결재/결재 취소/결재 재전송)
 *
 * 상태별 enable/사유는 guard에 이미 반영(disabled + tooltip).
 * `결재하기`(결재자 액션)는 별도 유지 — 이 빌더 대상 아님.
 */
export function buildApprovalActions(guard: ApprovalGuardResult): StickyHeaderAction[] {
  if (guard.hidden) return [];

  if (guard.mode === 'completion') {
    // 완료 흐름: 단일 버튼(취소/재전송 없음)
    return [
      {
        key: 'submit',
        label: '완료',
        onClick: guard.submit.onClick,
        variant: 'primary',
        disabled: !guard.submit.enabled,
        loading: guard.submit.busy,
        tooltip: guard.submit.reason,
      },
    ];
  }

  // 결재 흐름: 단일 `결재 ▼` 드롭다운
  const menuItems: NonNullable<StickyHeaderAction['menuItems']> = [
    {
      key: 'submit',
      label: '결재',
      onClick: guard.submit.onClick,
      disabled: !guard.submit.enabled,
      tooltip: guard.submit.reason,
    },
  ];

  if (guard.cancel) {
    menuItems.push({
      key: 'cancel',
      label: '결재 취소',
      onClick: guard.cancel.onClick,
      variant: 'danger',
      disabled: !guard.cancel.enabled,
      tooltip: guard.cancel.reason,
    });
  }

  if (guard.resend) {
    menuItems.push({
      key: 'resend',
      label: '결재 재전송',
      onClick: guard.resend.onClick,
      disabled: !guard.resend.enabled,
      tooltip: guard.resend.reason,
    });
  }

  return [
    {
      key: 'approval-menu',
      // 제출/재제출 진행 중에는 라벨과 스피너로 진행 상태를 보인다.
      // 상신 응답은 알림 발송을 기다리느라 수 초 걸릴 수 있어(2026-07-23 실측 4.07초),
      // 비활성만 두면 "안 눌린다"로만 보이고 진행 중임을 알 수 없다.
      label: guard.submit.busy ? '제출 중…' : '결재',
      loading: guard.submit.busy,
      disabled: guard.submit.busy,
      // 결재 가능 시기(submit.enabled — 예: TBM 교육 완료, 결재라인 존재)에만 primary로 강조.
      // 그 외(작성/교육 미완·이미 결재 요청됨 등)에는 secondary로 낮춰 표시.
      variant: guard.submit.enabled ? 'primary' : 'secondary',
      icon: createElement(DocumentArrowDownIcon, { className: 'h-4 w-4' }),
      // 메뉴 트리거 — 실제 클릭은 메뉴 토글로 처리(onClick 미사용)
      onClick: () => {},
      menuItems,
    },
  ];
}
